/**
 * Tools panel with tabbed sub-modes for drawing, routes, geofences, and measurement.
 * Active tab is panel-scoped local state (not Zustand).
 */

import { useState } from 'react';
import { Pencil, Route, ShieldAlert, Ruler, Spline } from 'lucide-react';
import { DrawingTab } from './tools/drawing-tab';
import { RouteTab } from './tools/route-tab';
import { GeofenceTab } from './tools/geofence-tab';
import { MeasurementTab } from './tools/measurement-tab';
import { BoundaryImportTab } from './tools/boundary-import-tab';

/** Sub-mode identifiers for tools panel tabs */
type ToolsTab = 'draw' | 'route' | 'geofence' | 'measure' | 'boundaries';

/** Tab metadata for rendering the tab selector */
const TABS: readonly { id: ToolsTab; label: string; icon: typeof Pencil }[] = [
  { id: 'draw', label: 'Draw', icon: Pencil },
  { id: 'route', label: 'Route', icon: Route },
  { id: 'geofence', label: 'Geofence', icon: ShieldAlert },
  { id: 'measure', label: 'Measure', icon: Ruler },
  { id: 'boundaries', label: 'Bounds', icon: Spline },
];

/** Tab content component mapping */
const TAB_CONTENT: Record<ToolsTab, () => React.JSX.Element> = {
  draw: DrawingTab,
  route: RouteTab,
  geofence: GeofenceTab,
  measure: MeasurementTab,
  boundaries: BoundaryImportTab,
};

/** Tools panel with tab selector and active tab content */
export function ToolsPanel(): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<ToolsTab>('draw');
  const TabContent = TAB_CONTENT[activeTab];

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex border-b border-white/10 px-2">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                isActive
                  ? 'border-b-2 border-echo-yellow text-echo-yellow'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>
      <TabContent />
    </div>
  );
}
